import { useMemo, useState, type ReactNode } from 'react';
import { Segmented } from 'antd';
import { AreaChartOutlined, BarChartOutlined, InboxOutlined } from '@ant-design/icons';

import { Bento, Tile, TileBody, TileHeader } from '../../../components/shared';
import { Empty, Select, Skeleton } from '../../../components/ui';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { soVN } from '../../../lib/dinh-dang';
import { useTokenTheoModel } from '../../goi-nap-token/hooks/useGoiNapToken';
import { BieuDoXepChong, ChuGiai, type CotXepChong } from '../../nhat-ky-truy-vet/chung/components/BieuDo';
import { KhoiLoi } from '../../nhat-ky-truy-vet/chung/components/KhoiLoi';
import { MAU_RA, MAU_VAO, NHAN_KHONG_GAN } from '../constants';
import { usePhanRa, useSuDungTheoNgay } from '../hooks/useTokenAi';
import {
  CAC_KHOANG_NGAY,
  cacThang,
  dayDuNgay,
  khoangNgay,
  ngayDayDu,
  ngayNgan,
  nhanThang,
  type DongXepHang,
  type KhoangNgay,
} from '../nghiep-vu/token-ai';
import type { TheoPhanRa } from '../types';
import { XepHangNgang } from './XepHangNgang';

/** Sổ theo ngày giữ 13 tháng (ADR-0123 mục 6). */
const SO_THANG_TRUOC = 12;
const SO_DONG_XEP_HANG = 8;

/** Vùng dữ liệu chung của mọi ô: đang tải, lỗi, rỗng, có dữ liệu. */
function VungO({
  dangTai,
  loi,
  thuLai,
  rong,
  children,
}: {
  dangTai: boolean;
  loi: unknown;
  thuLai: () => void;
  rong: boolean;
  children: ReactNode;
}) {
  const { t } = useT();
  if (dangTai) return <Skeleton className="h-44" aria-busy />;
  if (loi) return <KhoiLoi gon loi={loi} onThuLai={thuLai} />;
  if (rong) return <Empty icon={InboxOutlined} title={t('manTokenAi.rong')} className="py-8" />;
  return <>{children}</>;
}

function BieuDoTheoNgay({ tenantId }: { tenantId: string }) {
  const { t, locale } = useT();
  const [soNgay, setSoNgay] = useState<KhoangNgay>(30);
  // Chốt một lần cho mỗi khoảng: không đổi `queryKey` theo từng lần vẽ.
  const { from, to } = useMemo(() => khoangNgay(soNgay, Date.now()), [soNgay]);
  const q = useSuDungTheoNgay(tenantId, from, to);
  const vao = t('manTokenAi.dauVao');
  const ra = t('manTokenAi.dauRa');
  const dinhDang = (gt: number) => soVN(gt, locale);

  const cot = useMemo<CotXepChong[]>(
    () =>
      dayDuNgay(q.data ?? [], from, to).map((d) => ({
        nhan: ngayNgan(d.ngay),
        tieuDe: ngayDayDu(d.ngay),
        lat: [d.fox_dau_vao, d.fox_dau_ra],
        tong: d.fox_dau_vao + d.fox_dau_ra,
        moTa: t('manTokenAi.moTaDong', {
          ten: ngayDayDu(d.ngay),
          vao: soVN(d.fox_dau_vao, locale),
          ra: soVN(d.fox_dau_ra, locale),
          tong: soVN(d.fox_dau_vao + d.fox_dau_ra, locale),
          luot: soVN(d.calls, locale),
        }),
        theThem: [{ nhan: t('manTokenAi.soLuot'), giaTri: soVN(d.calls, locale), phu: '' }],
      })),
    // `t` là hàm mới mỗi lần vẽ; chữ chỉ đổi khi đổi ngôn ngữ.
    [q.data, from, to, locale],
  );
  const tongVao = (q.data ?? []).reduce((s, d) => s + d.fox_dau_vao, 0);
  const tongRa = (q.data ?? []).reduce((s, d) => s + d.fox_dau_ra, 0);

  return (
    <Tile>
      <TileHeader
        icon={<AreaChartOutlined />}
        tone="primary"
        title={t('manTokenAi.theoNgay')}
        action={
          <Segmented<KhoangNgay>
            size="small"
            aria-label={t('manTokenAi.chonKhoang')}
            value={soNgay}
            onChange={setSoNgay}
            options={CAC_KHOANG_NGAY.map((n) => ({ value: n, label: t('manTokenAi.soNgay', { so: n }) }))}
          />
        }
      />
      <TileBody>
        <VungO
          dangTai={q.isLoading}
          loi={q.error}
          thuLai={() => void q.refetch()}
          rong={(q.data ?? []).every((d) => d.fox_dau_vao + d.fox_dau_ra === 0)}
        >
          <BieuDoXepChong
            cot={cot}
            loat={[
              { nhan: vao, mau: MAU_VAO },
              { nhan: ra, mau: MAU_RA },
            ]}
            nhan={t('manTokenAi.theoNgayNhan', { so: soNgay })}
            nhanTong={t('manTokenAi.tong')}
            dinhDang={dinhDang}
            hienSoKhong
          />
          <ChuGiai
            muc={[
              { nhan: vao, mau: MAU_VAO, phu: soVN(tongVao, locale) },
              { nhan: ra, mau: MAU_RA, phu: soVN(tongRa, locale) },
            ]}
          />
        </VungO>
      </TileBody>
    </Tile>
  );
}

function OPhanRa({
  tenantId,
  thang,
  theo,
  tieuDe,
}: {
  tenantId: string;
  thang: string;
  theo: TheoPhanRa;
  tieuDe: KhoaDich;
}) {
  const { t } = useT();
  const q = usePhanRa(tenantId, thang, theo);
  const dong = useMemo<DongXepHang[]>(
    () =>
      (q.data ?? []).map((d) => ({
        khoa: d.khoa,
        ten: d.ten,
        vao: d.fox_dau_vao,
        ra: d.fox_dau_ra,
        tong: d.fox_tokens,
        luot: d.calls,
      })),
    [q.data],
  );
  return (
    <Tile span="lg:col-span-6">
      <TileHeader title={t(tieuDe)} />
      <TileBody>
        <VungO dangTai={q.isLoading} loi={q.error} thuLai={() => void q.refetch()} rong={dong.length === 0}>
          <XepHangNgang
            dong={dong}
            tran={SO_DONG_XEP_HANG}
            nhanKhongGan={t(NHAN_KHONG_GAN[theo])}
            nhanDanhSach={t(tieuDe)}
          />
        </VungO>
      </TileBody>
    </Tile>
  );
}

function OTheoModel({ tenantId, thang }: { tenantId: string; thang: string }) {
  const { t } = useT();
  const q = useTokenTheoModel(tenantId, true, thang);
  const dong = useMemo<DongXepHang[]>(
    () =>
      (q.data?.items ?? []).map((d) => ({
        khoa: d.model_key,
        ten: d.label ?? d.model_key,
        vao: d.fox_input,
        ra: d.fox_output,
        tong: d.fox_tokens,
        luot: d.calls,
      })),
    [q.data],
  );
  return (
    <Tile span="lg:col-span-6">
      <TileHeader title={t('manTokenAi.theoModel')} />
      <TileBody>
        <VungO dangTai={q.isLoading} loi={q.error} thuLai={() => void q.refetch()} rong={dong.length === 0}>
          <XepHangNgang
            dong={dong}
            tran={SO_DONG_XEP_HANG}
            nhanKhongGan={t('banToken.theoModel.khongModel')}
            nhanDanhSach={t('manTokenAi.theoModel')}
          />
        </VungO>
      </TileBody>
    </Tile>
  );
}

/**
 * Thẻ Mức sử dụng: biểu đồ theo ngày có khoảng riêng (7, 30, 90 ngày), bốn bảng xếp hạng dùng chung một ô chọn tháng vì
 * API chia theo tháng lịch. Mọi số là token Fox (ADR-0123 mục 1).
 */
export function TabMucSuDung({ tenantId }: { tenantId: string }) {
  const { t } = useT();
  const [cacLuaChon] = useState(() => cacThang(Date.now(), SO_THANG_TRUOC));
  const [thang, setThang] = useState(cacLuaChon[0] ?? '');
  const vao = t('manTokenAi.dauVao');
  const ra = t('manTokenAi.dauRa');

  return (
    <div className="space-y-4">
      <BieuDoTheoNgay tenantId={tenantId} />

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
        <div className="flex min-w-0 items-center gap-2">
          <BarChartOutlined aria-hidden className="text-primary-600" />
          <h2 className="text-sm font-semibold text-content-primary">{t('manTokenAi.phanTich')}</h2>
          <div className="[&>ul]:mt-0">
            <ChuGiai
              muc={[
                { nhan: vao, mau: MAU_VAO },
                { nhan: ra, mau: MAU_RA },
              ]}
            />
          </div>
        </div>
        <Select<string>
          className="w-32"
          aria-label={t('manTokenAi.chonThang')}
          value={thang}
          onChange={setThang}
          options={cacLuaChon.map((m) => ({ value: m, label: nhanThang(m) }))}
        />
      </div>

      <Bento>
        <OPhanRa tenantId={tenantId} thang={thang} theo="TINH_NANG" tieuDe="manTokenAi.theoTinhNang" />
        <OPhanRa tenantId={tenantId} thang={thang} theo="DON_VI" tieuDe="manTokenAi.theoPhongBan" />
        <OPhanRa tenantId={tenantId} thang={thang} theo="NGUOI_DUNG" tieuDe="manTokenAi.theoNguoiDung" />
        <OTheoModel tenantId={tenantId} thang={thang} />
      </Bento>
    </div>
  );
}
